/**
 * What: Draws what the Worker says about a field, for every field kind: its error, its warning, and what it is waiting on.
 * Transport / data-flow: Pure presentation. An error turns the field red through the Polaris `error` prop where the field has
 *   one (`fieldError`); the kinds without one (the tiles) draw it as a red line. A warning (amber) and the waiting text
 *   (subdued) are a line under the field (`BlockMessage`). The words are the Worker's; nothing is composed here. The
 *   exhaustive record below fails the type-check for a field kind that is not listed, so a new field kind cannot be added
 *   without saying how its message is drawn.
 */
import type { BuilderFieldBlock } from '@testfeed/shopify-contracts'
import { visibleMessage } from './visibleMessage'

/** How a field kind draws its error: `field` sets the field's own `error` prop; `line` draws a red line under it. */
const ERROR_DRAWN_BY = {
  select: 'field',
  number: 'field',
  text: 'field',
  checkbox: 'field',
  upload: 'field',
  tiles: 'line',
} as const satisfies Record<BuilderFieldBlock['kind'], 'field' | 'line'>

/** The text for the field's own `error` prop; undefined when it has no error, or draws it as a line. */
export function fieldError(block: BuilderFieldBlock, pending: ReadonlyMap<string, string>): string | undefined {
  const message = visibleMessage(block, pending)
  return message?.severity === 'error' && ERROR_DRAWN_BY[block.kind] === 'field' ? message.text : undefined
}

type Line = { tone: 'critical' | 'warning' | 'subdued'; text: string }

/** The line drawn under the field, or null: a warning, an error the field cannot show itself, or what it waits on. */
export function messageLine(block: BuilderFieldBlock, pending: ReadonlyMap<string, string>): Line | null {
  const message = visibleMessage(block, pending)
  if (message?.severity === 'warning') return { tone: 'warning', text: message.text }
  if (message?.severity === 'error' && ERROR_DRAWN_BY[block.kind] === 'line')
    return { tone: 'critical', text: message.text }
  return block.waitingOn ? { tone: 'subdued', text: block.waitingOn.text } : null
}

export function BlockMessage({ line }: { line: Line }) {
  return line.tone === 'subdued' ? (
    <s-text color="subdued">{line.text}</s-text>
  ) : (
    <s-text tone={line.tone}>{line.text}</s-text>
  )
}
